import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const NEXT = {
  pending: "preparing",
  preparing: "served",
  served: "completed",
};

const STATUS_COLOR = {
  pending: "#ff3b30",
  preparing: "#007aff",
  served: "#32d74b",
  completed: "#a1a1aa",
  cancelled: "#a1a1aa",
};

const STATUS_KEY = {
  pending: "status_pending",
  preparing: "status_preparing",
  served: "status_served",
  completed: "status_completed",
  cancelled: "status_cancelled",
};

export default function StaffOrders() {
  const { t } = useLang();
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);

  const advance = async (o) => {
    const next = NEXT[o.status];
    if (!next) return;
    try {
      await api.patch(`/orders/${o.id}/status?status=${next}`);
      toast.success(`${t("order_moved_to") || "Order moved to"} ${t(STATUS_KEY[next]) || next}`);
      load();
    } catch {
      toast.error(t("could_not_update") || "Could not update");
    }
  };

  return (
    <StaffLayout>
      <div className="overline text-zinc-500">{t("kitchen")}</div>
      <h1 className="font-condensed text-5xl font-bold tracking-tight mt-1">{t("orders_title")}</h1>

      <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {orders.length === 0 && (
          <div className="col-span-full border border-[#262626] p-10 text-center text-zinc-500 font-plex text-sm">
            {t("no_orders_yet")}
          </div>
        )}
        {orders.map((o) => (
          <div key={o.id} className="bg-[#121212] border border-[#262626] p-5" data-testid={`order-${o.id}`}>
            <div className="flex items-baseline justify-between">
              <div>
                <div className="overline text-zinc-500">{t("table")}</div>
                <div className="font-condensed font-bold text-3xl leading-none">
                  {String(o.table_number).padStart(2, "0")}
                </div>
              </div>
              <div className="text-right">
                <div className="overline text-zinc-500">{t("total")}</div>
                <div className="font-condensed font-bold text-2xl tabular-nums">€{o.total?.toFixed(2)}</div>
              </div>
            </div>

            <ul className="mt-4 space-y-1.5 border-t border-[#262626] pt-4">
              {o.items?.map((i, idx) => (
                <li key={idx} className="flex text-sm font-plex text-zinc-200" data-testid={`order-item-${idx}`}>
                  <span className="w-6 text-zinc-500 tabular-nums">{i.quantity}×</span>
                  <div className="flex-1 min-w-0 pr-2">
                    <div>{i.name}</div>
                    {i.description ? (
                      <div className="text-xs text-zinc-500 mt-0.5 leading-snug" data-testid={`order-item-description-${idx}`}>
                        {i.description}
                      </div>
                    ) : null}
                  </div>
                  <span className="text-zinc-500 tabular-nums">€{(i.price * i.quantity).toFixed(2)}</span>
                </li>
              ))}
            </ul>

            {o.notes ? (
              <div className="mt-3 border-l-2 border-[#c85a40] pl-3 text-xs text-zinc-400 font-plex italic">
                {o.notes}
              </div>
            ) : null}

            <div className="mt-5 flex items-center justify-between">
              <span className="overline" style={{ color: STATUS_COLOR[o.status] }}>{t(STATUS_KEY[o.status]) || o.status}</span>
              {NEXT[o.status] && (
                <button
                  onClick={() => advance(o)}
                  data-testid={`advance-order-${o.id}`}
                  className="border border-[#262626] px-3 py-2 font-plex text-xs hover:bg-white hover:text-black transition-colors"
                >
                  {t("mark")} {t(STATUS_KEY[NEXT[o.status]])}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </StaffLayout>
  );
}
